import { doc, DocumentData, getDocFromServer } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LoadingPage } from "../../components/loading.page";
import { NotFoundPage } from "../../components/404";
import { onAuthStateChanged, signInWithEmailAndPassword } from "firebase/auth";
import { firebaseAuth, firestoredb } from "../../utils/firebase";
import { toast } from "react-toastify";
import { CustomerHeader } from "../../components/header";
import { EventDetailScreen } from "./screens/event-details.screen";
import { BuyEventTicketScreen } from "./screens/buy-event-ticket.screen";
import { Helmet } from "react-helmet";
import { PaymentData } from "../../utils/types";
import { EventPaymentScreen } from "./screens/event-pay.screen";
import { PaymentSuccessScreen } from "./screens/payment-success.screen";

export const PAGE_STEPS: { [key: string]: string } = {
  DETAILS: "details",
  BUY: "buy",
  PAY: "pay",
  PAYMENT_SUCCESS: "payment-success",
};

const DIRECTION = {
  BACK: "back",
  FORWARD: "forward",
};

export function Event({ step = 0 }: { step?: number }) {
  const [pageLoading, setPageLoading] = useState(true);
  //page steps are ['details', 'buy', 'pay', 'payment-success']
  const [pageStep, setPageStep] = useState(
    step === 1 ? PAGE_STEPS.BUY : PAGE_STEPS.DETAILS
  );
  const { eventId } = useParams();
  const [eventExists, setEventExists] = useState(true);
  const [event, setEvent] = useState<DocumentData>({ picture: "" });
  const [paymentData] = useState<PaymentData | null>(null);

  useEffect(() => {
    loginFirebaseUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginFirebaseUser = async () => {
    try {
      // checks if a user is logged in and log them in
      const unsub = onAuthStateChanged(firebaseAuth, async (user) => {
        if (user) return await fetchEventData();
        if (!user) {
          await signInWithEmailAndPassword(
            firebaseAuth,
            process.env.REACT_APP_FIREBASE_AUTH_EMAIL!,
            process.env.REACT_APP_FIREBASE_AUTH_PASSWORD!
          );
          await fetchEventData();
        }
      });
      return () => unsub();
    } catch (error) {
      console.log("login firebase user error", error);
      toast.error("There was a problem logging in. Please contact admin");
    }
  };

  const fetchEventData = async () => {
    try {
      const eventRef = doc(firestoredb, "events", String(eventId));
      const docSnap = await getDocFromServer(eventRef);
      if (!docSnap.exists()) {
        setPageLoading(false);
        return setEventExists(false);
      }
      setEvent({ ...docSnap.data(), id: docSnap.id });
    } catch (error) {
      setPageLoading(false);
      console.log("fetch event data error", error);
      toast.error(
        "There was an error fetching the details of this event. Please contact support"
      );
    }
  };

  useEffect(() => {
    if (event?.id) setPageLoading(false);
  }, [event]);

  const handleMovingSteps = (action: string) => {
    if (!action) return;
    const currentValueIndex = Object.values(PAGE_STEPS).findIndex(
      (el) => el === pageStep
    );
    if (action === DIRECTION.FORWARD)
      setPageStep(PAGE_STEPS[Object.keys(PAGE_STEPS)[currentValueIndex + 1]]);
    if (action === DIRECTION.BACK && currentValueIndex > 0)
      setPageStep(PAGE_STEPS[Object.keys(PAGE_STEPS)[currentValueIndex - 1]]);
  };

  const handleNextSteps = () => {
    handleMovingSteps("forward");
  };

  return pageLoading ? (
    <LoadingPage />
  ) : !eventExists ? (
    <NotFoundPage type='event' />
  ) : (
    <div className=''>
      <Helmet>
        <title>{event?.title ?? ""} | TicketsbyDingpay</title>
        <meta name='description' content={event?.description ?? ""} />
        <meta
          property='og:title'
          content={`${event?.title ?? ""} | TicketsbyDingpay`}
        />
      </Helmet>
      <CustomerHeader
        themeStyle={pageStep === PAGE_STEPS.DETAILS ? event?.style : {}}
        pageStep={pageStep}
      />
      {pageStep === PAGE_STEPS.DETAILS ? (
        <EventDetailScreen event={event} handleNextSteps={handleNextSteps} />
      ) : pageStep === PAGE_STEPS.BUY ? (
        <BuyEventTicketScreen
          event={event}
          handleMoveToPreviousScreen={() => handleMovingSteps(DIRECTION.BACK)}
          setPageStep={(step) => setPageStep(step)}
        />
      ) : pageStep === PAGE_STEPS.PAY ? (
        <EventPaymentScreen
          paymentData={paymentData}
          setPageStep={setPageStep}
          handleCancelPayment={() => setPageStep(PAGE_STEPS.BUY)}
        />
      ) : pageStep === PAGE_STEPS.PAYMENT_SUCCESS ? (
        <PaymentSuccessScreen event={event} />
      ) : null}
    </div>
  );
}
